// Single source of truth for workflow Action-node display.
//
// Both the builder (config panel select + canvas node), the campaign
// "Workflow stages" view, and the AI workflow preview import from here, so the
// human-readable action labels can never drift apart across surfaces.

export const ACTION_TYPE_LABELS: Record<string, string> = {
  tag_contact: 'Add Tag',
  remove_tag: 'Remove Tag',
  move_to_list: 'Move to List',
  update_field: 'Update Contact Field',
  update_lead_score: 'Update Lead Score',
  mark_as_qualified: 'Mark as Qualified',
  mark_as_unqualified: 'Mark as Unqualified',
  // Kept even though the action is no longer offered (see ACTION_TYPE_GROUPS):
  // a workflow that already contains a Send Webhook node still has to render
  // its name on the canvas, in the campaign stages view and in the AI preview.
  // Dropping the label would show those nodes as a raw 'send_webhook' instead.
  send_webhook: 'Send Webhook',
  send_notification: 'Send Notification',
  add_to_campaign: 'Add to Campaign',
}

// Action types grouped for the config-panel <select>, labels resolved from the
// shared map above.
export const ACTION_TYPE_GROUPS: { group: string; values: string[] }[] = [
  { group: 'Contact Management', values: ['tag_contact', 'remove_tag', 'move_to_list', 'update_field'] },
  { group: 'Lead Scoring', values: ['update_lead_score', 'mark_as_qualified', 'mark_as_unqualified'] },
  // The whole Integrations group is withheld from the picker: Send Webhook,
  // Send Notification and Add to Campaign.
  //
  // Commented out rather than deleted, and only here. Every action still exists
  // in ACTION_TYPE_LABELS and in ActionConfig, so a workflow that already uses
  // one keeps working, keeps its name on the canvas and in the campaign stages
  // view, and can still be opened and edited. Only adding a NEW one is
  // withheld. Uncomment the line to offer the group again.
  // { group: 'Integrations', values: ['send_webhook', 'send_notification', 'add_to_campaign'] },
]

const SCORE_MODE_SIGN: Record<string, string> = {
  set: '=',
  increment: '+',
  decrement: '−',
}

/** Readable label for an action type alone (no config detail). */
export function actionTypeLabel(actionType?: string | null): string {
  if (!actionType) return 'Action'
  return ACTION_TYPE_LABELS[actionType] || actionType.replace(/_/g, ' ')
}

/**
 * One-line description of a configured action node — e.g. "Move to List →
 * Hot Leads", "Add Tag → VIP", "Update Lead Score +10". Falls back to the
 * action-type label, then "Action", when no config detail is available.
 *
 * Accepts the node's `data` object (with `action_type` + `action_config`).
 */
export function describeAction(
  data: { action_type?: string | null; action_config?: Record<string, any> | null } | null | undefined,
): string {
  const actionType = data?.action_type || ''
  const cfg = data?.action_config || {}
  const base = actionTypeLabel(actionType)

  switch (actionType) {
    case 'tag_contact':
    case 'remove_tag':
      return cfg.tag ? `${base} → ${cfg.tag}` : base
    case 'move_to_list':
      return cfg.list_name ? `${base} → ${cfg.list_name}` : base
    case 'update_field':
      if (!cfg.field) return base
      return `${base} → ${cfg.field}${cfg.value ? ` = ${cfg.value}` : ''}`
    case 'update_lead_score': {
      if (cfg.score_value == null || cfg.score_value === '') return base
      const sign = SCORE_MODE_SIGN[cfg.mode || 'increment'] ?? '+'
      return `${base} ${sign}${cfg.score_value}`
    }
    case 'send_webhook':
      return cfg.url ? `${base} → ${cfg.url}` : base
    case 'send_notification':
    case 'mark_as_qualified':
    case 'mark_as_unqualified':
    case 'add_to_campaign':
    default:
      return base
  }
}
